import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { ApiService } from '../utils/api';
import { formatDateTime } from '../utils/datetime';
import { formatCurrency } from '../utils/currency';

const ACTION_LABELS = {
  EDITED: 'Attendee edited details',
  CHANGE_REQUESTED: 'Attendee requested a paid change',
  CHANGE_APPROVED: 'Change approved',
  CHANGE_REJECTED: 'Change rejected',
  CANCELLED: 'Attendee cancelled',
  REFUND_INITIATED: 'Refund initiated',
  REFUNDED: 'Refund completed',
  REFUND_FAILED: 'Refund failed',
};

/** Changes made through "My registration" and the organiser's decisions on them, newest first. */
const RegistrationHistory = ({ eventId, userId, currency }) => {
  const { data: entries = [] } = useQuery({
    queryKey: ['registrationHistory', eventId, userId],
    queryFn: () => ApiService.getRegistrationHistory(eventId, userId),
    enabled: !!eventId && !!userId,
  });
  if (!entries.length) return null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-3 flex items-center gap-2 font-semibold text-gray-900">
        <History className="h-4 w-4" /> Change history
      </h3>
      <ol className="space-y-3">
        {entries.map((entry) => (
          <li key={entry.id} className="border-l-2 border-gray-200 pl-3 text-sm">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium text-gray-900">{ACTION_LABELS[entry.action] || entry.action}</span>
              <span className="text-xs text-gray-500">
                {formatDateTime(entry.at)}
                {entry.actor === 'ORGANISER' && entry.actorId ? ` · by ${entry.actorId}` : ''}
                {entry.actor === 'ATTENDEE' && entry.ip ? ` · IP ${entry.ip}` : ''}
              </span>
            </div>
            {entry.changes?.length > 0 && (
              <ul className="mt-1 space-y-0.5 text-gray-700">
                {entry.changes.map((change) => (
                  <li key={change.label}>
                    <span className="text-gray-500">{change.label}:</span>{' '}
                    <span className="line-through text-gray-400">{change.from || '—'}</span>
                    <span className="mx-1">→</span>
                    <span>{change.to || '—'}</span>
                  </li>
                ))}
              </ul>
            )}
            {entry.amountDue > 0 && (
              <div className="text-xs text-gray-600">Extra amount {formatCurrency(entry.amountDue, currency)}</div>
            )}
            {entry.note && <div className="text-xs text-gray-600">{entry.note}</div>}
          </li>
        ))}
      </ol>
    </div>
  );
};

export default RegistrationHistory;
